/* eslint-disable react-hooks/exhaustive-deps */
import Constant from '@/common/constant/Constant';
import TelephoneConstant from '@/common/constant/TelephoneConstant';
import MuiDateTimePicker from '../../common/form/datetime-picker';
import Utils from '@/common/utils/utils';
import { Button, IconButton, InputAdornment, TextField, Tooltip } from '@material-ui/core';
import { useEffect, useState } from 'react';
import RenderTooltipTitles from './RenderTooltipTitles';
import { PropTypes } from 'prop-types';
import moment from 'moment';
import { useRecoilValue } from 'recoil';
import { userAtom } from '@/state/atom/userAtom';
import TimezoneUtils from '@/common/utils/timezoneUtils';

DataPickerInput.propTypes = {
    defaultValue: PropTypes.any,
    clearErrors: PropTypes.any,
    name: PropTypes.any,
    data: PropTypes.any,
    triggerValidate: PropTypes.any,
    detailTicket: PropTypes.any,
    setValue: PropTypes.any,
    setOnChangeEdit: PropTypes.any,
    onChangeEdit: PropTypes.any,
    setShowButtonEditTicket: PropTypes.any,
    errorlist: PropTypes.any,
    getValues: PropTypes.func,
    pairedEndFieldId: PropTypes.string,
    displayMode: PropTypes.string,
};

export default function DataPickerInput(props) {
    const defaultValue = props?.defaultValue || '';

    let dateFormat = props.data.structure.dateFormat;
    if(props.data?.sysField === TelephoneConstant.TELEPHONE.SYSTEM_FIELD.DURATION){
        dateFormat = Constant.SETTING_DATE_FIELD_FORMAT.HHMMSS;
    }

    // TZL-001:「年月日＋時間（TimeZone）」— the form value is the stored UTC ISO string,
    // the picker shows the local time of the user timezone
    const userDetail = useRecoilValue(userAtom);
    const isTimeZoneFormat = dateFormat === Constant.SETTING_DATE_FIELD_FORMAT.YYYYMMDD_HHMM_TZ;
    const userTimeZone = TimezoneUtils.resolveUserTimezone(userDetail?.userSetting);

    // TZL-001: read-only system date-time fields keep their stored display type but are rendered as text in the user timezone.
    // Returns the text format of the field, '' for any other field
    const getReadOnlyTimeFormat = sysField => {
        switch (sysField) {
            case TelephoneConstant.TELEPHONE.SYSTEM_FIELD.START_TIME:
            case TelephoneConstant.TELEPHONE.SYSTEM_FIELD.END_TIME:
                return Constant.MOMENT_DATE_FORMAT.YYYYMMDD_HHMMSS;
            case Constant.SYSTEM_FIELD.KB_XML_TIMESTAMP:
                return Constant.MOMENT_DATE_FORMAT.YYYYMMDD_HHMM;
            default:
                return '';
        }
    };
    const readOnlyTimeFormat = getReadOnlyTimeFormat(props.data?.sysField);
    const isReadOnlyTimeField = Boolean(readOnlyTimeFormat);

    /**
     * TZL-001: UTC ISO string → moment of the local time in the user timezone (picker value).
     * Other formats / empty / already a moment / not a UTC ISO string: returned as is.
     */
    const toPickerValue = rawValue => {
        if (!isTimeZoneFormat || !rawValue || moment.isMoment(rawValue)) return rawValue;
        const localText = TimezoneUtils.formatUtcToLocal(rawValue, userTimeZone);
        return localText ? moment(localText, Constant.MOMENT_DATE_FORMAT.YYYYMMDD_HHMM_TZ) : rawValue;
    };

    /**
     * TZL-001: current form value (stored UTC ISO string), falls back to defaultValue when the form value is not readable
     * (no getValues, or the field is not registered yet). A cleared field ('') stays cleared.
     */
    const getFormUtcValue = () => {
        const formValue = props.getValues?.(props?.name);
        return typeof formValue === 'string' ? formValue : defaultValue;
    };

    const [value, setValue] = useState(toPickerValue(defaultValue));

    // TZL-001: the user timezone can resolve after mount (userAtom loads asynchronously) or change
    // → rebuild the picker local time from the stored UTC value
    useEffect(() => {
        if (!isTimeZoneFormat) return;
        setValue(toPickerValue(getFormUtcValue() || null));
    }, [userTimeZone]);

    const isAddTicket = !props?.detailTicket || false;

    const pickerConfig = {
        [Constant.SETTING_DATE_FIELD_FORMAT.YYYYMMDD_HHMM]: {
            typePicker: Constant.COMPONENT_DATETIME_PICKER.DATE_TIME_PICKER,
            format: Constant.MOMENT_DATE_FORMAT.YYYYMMDD_HHMM,
            showClearButton: true,
            useValueAsDefault: false
        },
        [Constant.SETTING_DATE_FIELD_FORMAT.YYYYMMDD]: {
            typePicker: Constant.COMPONENT_DATETIME_PICKER.DATE_PICKER,
            format: Constant.MOMENT_DATE_FORMAT.YYYYMMDD,
            showClearButton: true,
            useValueAsDefault: false
        },
        [Constant.SETTING_DATE_FIELD_FORMAT.YYYYMM]: {
            typePicker: Constant.COMPONENT_DATETIME_PICKER.DATE_PICKER,
            format: Constant.MOMENT_DATE_FORMAT.YYYYMM,
            views: [Constant.VIEW_DATE_PICKER.YEAR, Constant.VIEW_DATE_PICKER.MONTH],
            showClearButton: true,
            useValueAsDefault: false
        },
        [Constant.SETTING_DATE_FIELD_FORMAT.YYYY]: {
            typePicker: Constant.COMPONENT_DATETIME_PICKER.DATE_PICKER,
            format: Constant.MOMENT_DATE_FORMAT.YYYY,
            views: [Constant.VIEW_DATE_PICKER.YEAR],
            showClearButton: true,
            useValueAsDefault: false
        },
        [Constant.SETTING_DATE_FIELD_FORMAT.HHMM]: {
            typePicker: Constant.COMPONENT_DATETIME_PICKER.TIME_PICKER,
            format: Constant.MOMENT_DATE_FORMAT.HHMM,
            showClearButton: true,
            useValueAsDefault: false
        },
        [Constant.SETTING_DATE_FIELD_FORMAT.YYYYMMDD_HHMMSS]: {
            typePicker: Constant.COMPONENT_DATETIME_PICKER.DATE_TIME_PICKER,
            format: Constant.MOMENT_DATE_FORMAT.YYYYMMDD_HHMMSS,
            showClearButton: false,
            useValueAsDefault: false
        },
        [Constant.SETTING_DATE_FIELD_FORMAT.HHMMSS]: {
            typePicker: Constant.COMPONENT_DATETIME_PICKER.TIME_PICKER,
            format: Constant.MOMENT_DATE_FORMAT.HHMMSS,
            showClearButton: false,
            useValueAsDefault: false
        },
        // TZL-001: value is a moment of the user local time (see toPickerValue); defaultValue stays the UTC ISO form value
        [Constant.SETTING_DATE_FIELD_FORMAT.YYYYMMDD_HHMM_TZ]: {
            typePicker: Constant.COMPONENT_DATETIME_PICKER.DATE_TIME_PICKER,
            format: Constant.MOMENT_DATE_FORMAT.YYYYMMDD_HHMM_TZ,
            showClearButton: true,
            useValueAsDefault: false
        }
    };

    // Default config
    const defaultConfig = {
        typePicker: Constant.COMPONENT_DATETIME_PICKER.DATE_TIME_PICKER,
        format: Constant.MOMENT_DATE_FORMAT.YYYYMMDD_HHMM,
        showClearButton: true,
        useValueAsDefault: true
    };

    const getDateByFormat = (dateValue, format) => {
        if (moment.isMoment(dateValue)) {
            // keep the original date value even if the format is invalid when onchange
            if (!dateValue.isValid()) {
                return dateValue._i || '';
            }
        }
        let dateByFormat = '';
        switch (format) {
            case Constant.SETTING_DATE_FIELD_FORMAT.YYYYMMDD_HHMM:
                dateByFormat = dateValue.format(Constant.MOMENT_DATE_FORMAT.YYYYMMDD_HHMM);
                break;
            case Constant.SETTING_DATE_FIELD_FORMAT.YYYYMMDD:
                dateByFormat = dateValue.format(Constant.MOMENT_DATE_FORMAT.YYYYMMDD);
                break;
            case Constant.SETTING_DATE_FIELD_FORMAT.YYYYMM:
                dateByFormat = dateValue.format(Constant.MOMENT_DATE_FORMAT.YYYYMM);
                break;
            case Constant.SETTING_DATE_FIELD_FORMAT.YYYY:
                dateByFormat = dateValue.format(Constant.MOMENT_DATE_FORMAT.YYYY);
                break;
            case Constant.SETTING_DATE_FIELD_FORMAT.HHMM:
                dateByFormat = dateValue.format(Constant.MOMENT_DATE_FORMAT.HHMM);
                break;
            case Constant.SETTING_DATE_FIELD_FORMAT.YYYYMMDD_HHMMSS:
                dateByFormat = dateValue.format(Constant.MOMENT_DATE_FORMAT.YYYYMMDD_HHMMSS);
                break;
            case Constant.SETTING_DATE_FIELD_FORMAT.HHMMSS:
                dateByFormat = dateValue.format(Constant.MOMENT_DATE_FORMAT.HHMMSS);
                break;
            case Constant.SETTING_DATE_FIELD_FORMAT.YYYYMMDD_HHMM_TZ: {
                // TZL-001: the picker shows the local time of the user timezone → store as UTC ISO string.
                // Not convertible (e.g. year < 1000) → keep the local text so the schema reports the format error
                const localText = dateValue.format(Constant.MOMENT_DATE_FORMAT.YYYYMMDD_HHMM_TZ);
                dateByFormat = TimezoneUtils.convertLocalToUtc(localText, userTimeZone) || localText;
                break;
            }
            default:
                dateByFormat = dateValue.format(Constant.MOMENT_DATE_FORMAT.YYYYMMDD_HHMM);
        }
        return dateByFormat;
    };

    const onChangeDate = (dateValue, format) => {
        props.clearErrors(props?.name);

        let dateByFormat = dateValue ? getDateByFormat(dateValue, format) : '';

        setValue(dateValue);
        props.setValue(props?.name, dateByFormat);

        // Auto-adjust end_date when start_date > end_date for AI Telephone date fields
        const sysField = props?.data?.sysField;
        if (
            sysField === Constant.SYSTEM_FIELD.AI_TELEPHONE_RECEIVING_CALL_EXECUTION_START_DATE &&
            props?.pairedEndFieldId &&
            dateValue &&
            props?.getValues
        ) {
            const endDateValue = props.getValues(props.pairedEndFieldId);
            if (endDateValue) {
                const startMoment = moment.isMoment(dateValue) ? dateValue : moment(dateValue, format);
                const endMoment = moment(endDateValue, format);

                if (startMoment.isValid() && endMoment.isValid() && startMoment.isAfter(endMoment)) {
                    props.setValue(props.pairedEndFieldId, dateByFormat);
                }
            }
        }

        if (props?.setIsEdited) {
            if(props?.setOnChangeEdit){
                    props?.setOnChangeEdit(!props.onChangeEdit)
                }
            props?.setIsEdited(true);
            props?.setShowButtonEditTicket && props?.setShowButtonEditTicket()
            if(!props?.listFieldEdited?.includes(props?.name)){
                props?.setListFieldEdited([...props.listFieldEdited,props?.name])
            }
        }
    };

    const onBlurValidateDate = (fieldName) => {
        if (props.triggerValidate) {
            props.triggerValidate(fieldName)
        }
    }

    const handleCloseDatePicker = () => {
        setValue(null);
        props.setValue(props?.name, '');
        if (props?.setIsEdited) {
            if (props?.setOnChangeEdit) {
                props?.setOnChangeEdit(!props.onChangeEdit);
            }
            props?.setIsEdited(true);
            props?.setShowButtonEditTicket && props?.setShowButtonEditTicket();
            if (!props?.listFieldEdited?.includes(props?.name)) {
                props?.setListFieldEdited([...props.listFieldEdited, props?.name]);
            }
        }
    }

    useEffect(() => {
        props.register(props?.name);
        props.setValue(props?.name, props?.defaultValue ? props?.defaultValue : '');
        setValue(toPickerValue(props?.defaultValue ? props?.defaultValue : ''));
    }, [props?.defaultValue]);

    // Sync local state when subscription updates field value
    useEffect(() => {
        if (props?.subscriptionFieldValue === undefined) return;
        if (props?.subscriptionFieldValue !== value) {
            let parsedValue = props?.subscriptionFieldValue || null;
            // Parse time string for HHMMSS/HHMM formats (e.g. '00:00:37')
            if (parsedValue && [Constant.SETTING_DATE_FIELD_FORMAT.HHMMSS, Constant.SETTING_DATE_FIELD_FORMAT.HHMM].includes(dateFormat)) {
                const format = dateFormat === Constant.SETTING_DATE_FIELD_FORMAT.HHMMSS
                    ? Constant.MOMENT_DATE_FORMAT.HHMMSS
                    : Constant.MOMENT_DATE_FORMAT.HHMM;
                parsedValue = moment(parsedValue, format);
            }
            setValue(toPickerValue(parsedValue));
            props.setValue(props?.name, props?.subscriptionFieldValue || '');
        }
    }, [props?.subscriptionFieldValue]);

    useEffect(() => {
        setValue(toPickerValue(props.defaultValue ? props.defaultValue: null))
    },[props?.triggerReloadTicket])

    useEffect(() => {
        if (isAddTicket) {
            props.register(props?.name, {
                onChange: e => {}
            });
        
            setValue(toPickerValue(defaultValue || null))
            props.setValue(props?.name, defaultValue || '');
        }
        let isKnowledgeForm = [Constant.FORM_TYPE.FORM_KNOWLEDGE, Constant.FORM_TYPE.FORM_MASTER].includes(props?.parentForm?.type);
        if (props?.isDraftMode || (props?.editTicket && !isKnowledgeForm)) {
            props.register(props?.name, {
                onChange: e => {}
            });
        
            setValue(toPickerValue(defaultValue || null))
            props.setValue(props?.name, defaultValue || '');
        }
    }, [props?.parentForm]);

    const isDisableField = () => {
        return (
            props.data.structure.publicForCustomer == 2 ||
            props.viewFromProjectOfEscalateTicket ||
            !Utils.isEmptyString(props.detailTicket?.deletedAt) ||
            props.hasEditFieldPermissions === false
        );
    };

    useEffect(() => {
        if (props?.flagReset) {
            props?.setValue(props?.name, props?.defaultValue || null);
            if (dateFormat === Constant.SETTING_DATE_FIELD_FORMAT.HHMM) {
                setValue(Utils.parseDate({ value: props?.defaultValue, format: 'HH:mm' }));
            } else {
                setValue(toPickerValue(props?.defaultValue || null));
            }
            props.setFlagReset(false);
        }
    }, [props?.flagReset, props?.defaultValue, props?.name]);

    const getComponentPicker = () => {
        const config = pickerConfig[dateFormat] || defaultConfig;

        const isPickerDisabled = () => {
            return (
                props.data.structure.publicForCustomer === 
                    Constant.SETTING_FORM_DEFAULT.READ_ONLY ||
                props.viewFromProjectOfEscalateTicket ||
                !Utils.isEmptyString(props.detailTicket?.deletedAt) ||
                props.hasEditFieldPermissions === false
            );
        };

        const shouldHideIcon = () => {
            // Only hide icon for telephone_duration field
            return (
                props.data?.sysField === 
                    TelephoneConstant.TELEPHONE.SYSTEM_FIELD.DURATION
            );
        };

        const renderClearButton = () => {
            if (!config?.showClearButton) return null;

            if (props.hasEditFieldPermissions && !isDisableField()) {
                return (
                    <Button
                        disabled={isDisableField()}
                        className="close-DatePicker"
                        onClick={handleCloseDatePicker}
                    >
                        <img src={Constant.ICON_CLOSE_DATETIME} alt="" />
                    </Button>
                );
            }
            return null;
        };

        const hideIconClass = shouldHideIcon() ? 'hide-date-picker-icon' : '';

        /**
         * TZL-001 (BR-55): view mode of「年月日＋時間（TimeZone）」shows the value with the offset label,
         * e.g. '2026/09/08 12:00 (UTC+07:00)'. A read-only text field is shown because the keyboard picker
         * masks every non-digit character of its format. The picker stays mounted (hidden) so the
         * react-hook-form Controller keeps its value. Edit mode: normal picker, no label.
         */
        const isViewMode = props?.displayMode === Constant.DISPLAY_MODE.VIEW;
        // TZL-001: read-only system date-time fields (call start/end time, KB-XML timestamp) are always shown as text in the user timezone
        const isTimeZoneViewMode = (isTimeZoneFormat && isViewMode) || isReadOnlyTimeField;
        // Format the stored UTC value directly: a round trip through the picker local time would pick the
        // first occurrence of a DST-repeated hour and use the timezone the picker value was built with.
        // A value that is not UTC ISO (saved before the display type was switched to TimeZone) is shown as is, no conversion
        const getTimeZoneViewText = () => {
            const formUtcValue = getFormUtcValue();
            if (isReadOnlyTimeField) {
                // Offset label only in view mode (AC-35), value without an offset (local text) is shown as is
                if (!TimezoneUtils.isUtcIsoString(formUtcValue)) return typeof formUtcValue === 'string' ? formUtcValue : '';
                const formatReadOnlyTime = isViewMode ? TimezoneUtils.formatUserDateTimeWithOffset : TimezoneUtils.formatUserDateTime;
                return formatReadOnlyTime(formUtcValue, readOnlyTimeFormat, userDetail?.userSetting) || formUtcValue;
            }
            return TimezoneUtils.formatUtcToLocal(formUtcValue, userTimeZone, true) ||
                (typeof formUtcValue === 'string' ? formUtcValue : '');
        };
        
        return (
            <div className={`itemDatePicker ${hideIconClass}`}>
                {isTimeZoneViewMode && (
                    <TextField
                        className="field-date-picker form-control-wrapPicker form-control-hg40"
                        variant="outlined"
                        value={getTimeZoneViewText()}
                        disabled
                        fullWidth
                        InputProps={{
                            readOnly: true,
                            // Same adornment structure as the picker → same calendar icon (styled by .form-control-wrapPicker)
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton disabled />
                                </InputAdornment>
                            )
                        }}
                    />
                )}
                <div style={isTimeZoneViewMode ? { display: 'none' } : {}}>
                    <MuiDateTimePicker
                        className="field-date-picker form-control-wrapPicker form-control-hg40"
                        name={props.name}
                        defaultValue={config?.useValueAsDefault ? value : defaultValue}
                        value={value}
                        typePicker={config?.typePicker}
                        format={config?.format}
                        {...(config?.views && { views: config?.views })}
                        ampm={false}
                        disabled={isPickerDisabled() || config?.disabled}
                        onChange={e => onChangeDate(e, dateFormat)}
                        onBlur={e => onBlurValidateDate(props.name)}
                        errorlist={props.errorlist}
                        hideIcon={shouldHideIcon()}
                    />
                </div>
                {!isTimeZoneViewMode && renderClearButton()}
            </div>
        );
    };

    const getWrapperClassName = () => {
        const sysField = props.data?.sysField;
        const ticketMetaData = Utils.convertJSONDataToObject(props?.detailTicket?.metaData);

        const classConditions = [
            {
                condition: sysField === TelephoneConstant.TELEPHONE.SYSTEM_FIELD.START_TIME,
                className: 'width-50-left'
            },
            {
                condition: sysField === TelephoneConstant.TELEPHONE.SYSTEM_FIELD.END_TIME,
                className: 'width-50-right'
            },
            {
                condition:
                    sysField === TelephoneConstant.TELEPHONE.SYSTEM_FIELD.DURATION &&
                    (ticketMetaData?.telephoneType === Constant.TICKET_PROPERTY.TRAINING ||
                        Utils.isAppIntegrationTicket(props?.detailTicket)),
                className: 'width-50-right'
            }
        ];
        return classConditions
            .filter(({ condition }) => condition)
            .map(({ className }) => className)
            .join(' ');
    };

    return (
        <div className={`form-addTicket ${getWrapperClassName()}`}>
            <div className="cell-prod-status gap-8">
                {props?.isTooltipTitle ? 
                    <RenderTooltipTitles {...props}/> :
                    <label className="form-addTicket-label w-f100">{props?.label} {props.data.structure.required && <span className="required">*</span>}</label>
                }
                {props?.isShowIconCopyFieldId && (
                    <Tooltip title={props?.data.id} placement='top' arrow>
                        <img
                            style={{paddingTop: '10px'}}
                            src="/assets/image/icon-copy.svg"
                            alt="copy"
                            onClick={() => navigator.clipboard.writeText(props?.data.id)}
                        />
                    </Tooltip>
                )}
            </div>
            
            <div className="form-addTicket-input">{getComponentPicker()}</div>
        </div>
    );
}
